import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Lightbulb, RefreshCw, X } from 'lucide-react'

/**
 * DailyTip Component
 * Shows quirky survival tips with humor
 */

const survivalTips = [
  {
    tip: "Pro tip: You can't drink lava. Try boiling water instead.",
    category: "basics",
    icon: "🌋"
  },
  {
    tip: "The average person can survive 3 weeks without food, 3 days without water... but only 3 minutes without air.",
    category: "facts",
    icon: "⏱️"
  },
  {
    tip: "In a nuclear blast, don't hide behind a window. Glass doesn't block radiation, but it does make excellent shrapnel.",
    category: "nuclear",
    icon: "☢️"
  },
  {
    tip: "Your car is NOT a good shelter from tornadoes. Unless you enjoy being Dorothy without the ruby slippers.",
    category: "weather",
    icon: "🌪️"
  },
  {
    tip: "Moss doesn't always grow on the north side of trees. Trust a compass, not a plant.",
    category: "navigation",
    icon: "🧭"
  },
  {
    tip: "Running zigzag from an alligator is a myth. Just run straight - and faster than your friend.",
    category: "wildlife",
    icon: "🐊"
  },
  {
    tip: "In a zombie apocalypse, cardio beats karate. Rule #1: Cardio.",
    category: "humor",
    icon: "🧟"
  },
  {
    tip: "Fire needs oxygen. So do you. Choose wisely when lighting fires indoors.",
    category: "fire",
    icon: "🔥"
  },
  {
    tip: "Drinking your own urine won't save you. It just makes a bad situation taste worse.",
    category: "hydration",
    icon: "💧"
  },
  {
    tip: "The 'five-second rule' doesn't apply in a fallout zone. Leave the radioactive Twinkie.",
    category: "radiation",
    icon: "☢️"
  },
  {
    tip: "Duct tape fixes everything except radiation poisoning and broken hearts.",
    category: "tools",
    icon: "🔧"
  },
  {
    tip: "In an earthquake: Drop, Cover, Hold On. Not 'Instagram, Scream, Run Around'.",
    category: "earthquake",
    icon: "🏚️"
  },
  {
    tip: "Your phone's flashlight drains battery fast. Save it for emergencies, not TikToks.",
    category: "tech",
    icon: "📱"
  },
  {
    tip: "Hypothermia can occur in 50°F water. The ocean doesn't care about your beach body.",
    category: "cold",
    icon: "🥶"
  },
  {
    tip: "Canned food lasts years. Your roommate's leftover pizza does not.",
    category: "food",
    icon: "🥫"
  }
]

const DailyTip = ({ className = '', onClose }) => {
  const [currentTip, setCurrentTip] = useState(null)
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    // Get today's tip based on date (consistent for all users on the same day)
    const today = new Date()
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000)
    const tipIndex = dayOfYear % survivalTips.length
    setCurrentTip(survivalTips[tipIndex])
  }, [])

  const getRandomTip = () => {
    const newTip = survivalTips[Math.floor(Math.random() * survivalTips.length)]
    setCurrentTip(newTip)
  }

  const handleClose = () => {
    setIsVisible(false)
    if (onClose) onClose()
  }

  if (!currentTip || !isVisible) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`daily-tip-container ${className}`}
      >
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-4 border border-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
              </div>
              <h3 className="font-bold text-white">Daily Survival Wisdom</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-300 transition-colors"
              aria-label="Close tip"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Tip Content */}
          <motion.div
            key={currentTip.tip}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-start gap-3"
          >
            <span className="text-2xl">{currentTip.icon}</span>
            <p className="text-gray-300 flex-1 leading-relaxed">
              {currentTip.tip}
            </p>
          </motion.div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-gray-500 uppercase tracking-wider">
              #{currentTip.category}
            </span>
            <button
              onClick={getRandomTip}
              className="flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300 transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Another tip
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

// Simplified version for embedding in smaller spaces
export const MiniDailyTip = ({ className = '' }) => {
  const [tip, setTip] = useState(null)

  useEffect(() => {
    const randomTip = survivalTips[Math.floor(Math.random() * survivalTips.length)]
    setTip(randomTip)
  }, [])

  if (!tip) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`bg-gray-800 rounded-lg p-3 ${className}`}
    >
      <div className="flex items-center gap-2 text-sm">
        <span className="text-lg">{tip.icon}</span>
        <p className="text-gray-300 line-clamp-2">{tip.tip}</p>
      </div>
    </motion.div>
  )
}

export default DailyTip